import { Box, Typography } from '@mui/material'
import { FC } from 'react'
import { DailyCount } from '../../types/apiRequests'
import { border } from '../../layout/theme/border'
import { xxllncColor } from '../../layout/theme/color'
import { fadeSlideIn, growBar } from './animations'

export const WeeklyChart: FC<{
  daily: DailyCount[]
  mcpWeek: number
  workflowWeek: number
  delay: number
  translate: (key: string) => string
}> = ({ daily, mcpWeek, workflowWeek, delay, translate }) => {
  const maxVal = Math.max(...daily.map(d => d.mcp + (d.workflow ?? 0)), 1)
  const chartH = 140

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: border.card.radius,
        border: '1px solid rgba(0,0,0,0.06)',
        p: 3,
        animation: `${fadeSlideIn} 0.5s ease-out ${delay}ms both`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: 'rgba(0,0,0,0.3)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 0.5,
            }}
          >
            {translate('dashboard.sectionTraffic')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
            {translate('dashboard.requestsThisWeek')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: xxllncColor.dark100,
              }}
            />
            <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>MCP</Typography>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: xxllncColor.dark100,
                fontVariantNumeric: 'tabular-nums',
                ml: 0.5,
              }}
            >
              {mcpWeek}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: xxllncColor.orange100,
              }}
            />
            <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>Workflow</Typography>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: xxllncColor.orange100,
                fontVariantNumeric: 'tabular-nums',
                ml: 0.5,
              }}
            >
              {workflowWeek}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          height: chartH,
          px: 1,
        }}
      >
        {daily.map((day, i) => {
          const mcpH = (day.mcp / maxVal) * chartH
          const workflowH = ((day.workflow ?? 0) / maxVal) * chartH
          const isToday = i === daily.length - 1
          return (
            <Box
              key={day.date}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', gap: '3px', alignItems: 'flex-end', height: chartH }}>
                <Box
                  sx={{
                    width: 16,
                    height: Math.max(mcpH, 2),
                    bgcolor: isToday ? xxllncColor.dark100 : xxllncColor.opDark80,
                    borderRadius: '4px 4px 0 0',
                    transformOrigin: 'bottom',
                    animation: `${growBar} 0.6s ease-out ${delay + 100 + i * 60}ms both`,
                  }}
                />
                <Box
                  sx={{
                    width: 16,
                    height: Math.max(workflowH, 2),
                    bgcolor: isToday ? xxllncColor.orange100 : xxllncColor.opOrange80,
                    borderRadius: '4px 4px 0 0',
                    transformOrigin: 'bottom',
                    animation: `${growBar} 0.6s ease-out ${delay + 130 + i * 60}ms both`,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: isToday ? 700 : 500,
                  color: isToday ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  mt: 0.75,
                }}
              >
                {day.date}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
